import type { TextRun } from "@bezpart-board/shared";
import { objectRuns } from "../textRuns";
import type { SceneObject } from "../SceneHost";
import { clampBounds, type Bounds } from "../selectionBounds";
import { usesRotationField } from "./rotationField";

export type ResizeHandleId = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

/** Совпадает с MIN_SIZE в clampBounds — иначе clamp снова перекосит пропорции. */
const MIN_RESIZE_SIZE = 16;

function isCornerHandle(handle: ResizeHandleId): boolean {
  return handle.length === 2;
}

/** Resize bbox by handle drag; Shift — пропорции, Alt — от центра. */
export function boundsFromResizeHandle(
  handle: ResizeHandleId,
  start: Bounds,
  pointer: { x: number; y: number },
  shiftKey: boolean,
  altKey: boolean
): Bounds {
  const cx = start.x + start.w / 2;
  const cy = start.y + start.h / 2;

  if (altKey) {
    let halfW = start.w / 2;
    let halfH = start.h / 2;

    if (handle.includes("e") || handle.includes("w") || isCornerHandle(handle)) {
      halfW = Math.max(8, Math.abs(pointer.x - cx));
    }
    if (handle.includes("n") || handle.includes("s") || isCornerHandle(handle)) {
      halfH = Math.max(8, Math.abs(pointer.y - cy));
    }
    if (handle === "n" || handle === "s") {
      halfW = start.w / 2;
    }
    if (handle === "e" || handle === "w") {
      halfH = start.h / 2;
    }

    if (shiftKey) {
      const ratio = start.w / Math.max(start.h, 1);
      if (isCornerHandle(handle)) {
        const size = Math.max(halfW, halfH);
        halfW = size;
        halfH = size;
      } else if (handle === "e" || handle === "w") {
        halfH = halfW / ratio;
      } else if (handle === "n" || handle === "s") {
        halfW = halfH * ratio;
      }
    }

    return clampBounds({
      x: Math.round(cx - halfW),
      y: Math.round(cy - halfH),
      w: Math.round(halfW * 2),
      h: Math.round(halfH * 2),
    });
  }

  let x = start.x;
  let y = start.y;
  let x2 = start.x + start.w;
  let y2 = start.y + start.h;

  if (handle.includes("w")) x = pointer.x;
  if (handle.includes("e")) x2 = pointer.x;
  if (handle.includes("n")) y = pointer.y;
  if (handle.includes("s")) y2 = pointer.y;

  if (handle === "n" || handle === "s") {
    x = start.x;
    x2 = start.x + start.w;
  }
  if (handle === "e" || handle === "w") {
    y = start.y;
    y2 = start.y + start.h;
  }

  let nx = Math.min(x, x2);
  let ny = Math.min(y, y2);
  let nw = Math.abs(x2 - x);
  let nh = Math.abs(y2 - y);

  const ratio = start.w / Math.max(start.h, 1);

  if (shiftKey) {
    if (isCornerHandle(handle)) {
      if (nw / nh > ratio) nw = nh * ratio;
      else nh = nw / ratio;
      if (handle.includes("w")) nx = x2 - nw;
      if (handle.includes("n")) ny = y2 - nh;
    } else if (handle === "e" || handle === "w") {
      nh = nw / ratio;
      ny = start.y + (start.h - nh) / 2;
    } else if (handle === "n" || handle === "s") {
      nw = nh * ratio;
      nx = start.x + (start.w - nw) / 2;
    }
  }

  return clampBounds({ x: nx, y: ny, w: nw, h: nh });
}

export function scaleSnapshots(
  startGroup: Bounds,
  nextGroup: Bounds,
  snapshots: Map<string, Bounds>
): Map<string, { x: number; y: number; w: number; h: number }> {
  const sx = startGroup.w > 0 ? nextGroup.w / startGroup.w : 1;
  const sy = startGroup.h > 0 ? nextGroup.h / startGroup.h : 1;
  const out = new Map<string, { x: number; y: number; w: number; h: number }>();
  snapshots.forEach((b, id) => {
    out.set(id, {
      x: Math.round(nextGroup.x + (b.x - startGroup.x) * sx),
      y: Math.round(nextGroup.y + (b.y - startGroup.y) * sy),
      w: Math.max(16, Math.round(b.w * sx)),
      h: Math.max(16, Math.round(b.h * sy)),
    });
  });
  return out;
}

export type ObjectTransformSnapshot = {
  x: number;
  y: number;
  w: number;
  h: number;
  rotation?: number;
  points?: [number, number][];
  /** Стартовый размер шрифта для text/sticky — масштабируется вместе с рамкой. */
  fontSize?: number;
  /** Полный meta текста — чтобы textWidthMode / href не терялись при undo ресайза. */
  meta?: Record<string, unknown>;
  /**
   * Куски надписи со своими размерами — масштабируются вместе с рамкой,
   * и отмена растягивания возвращает их прежними.
   */
  runs?: TextRun[];
};

/** Совпадает с API `style.fontSize` (sceneSchema): иначе commit падает с «Некорректные данные». */
const TEXT_FONT_MIN = 8;
const TEXT_FONT_MAX = 300;

/** Масштаб шрифта при ресайзе рамки (геометрическое среднее sx/sy). */
export function fontScaleFromGroupResize(startGroup: Bounds, nextGroup: Bounds): number {
  const sx = startGroup.w > 0 ? nextGroup.w / startGroup.w : 1;
  const sy = startGroup.h > 0 ? nextGroup.h / startGroup.h : 1;
  return Math.sqrt(Math.max(1e-6, sx) * Math.max(1e-6, sy));
}

/**
 * Единый масштаб для драга за угол: берём ту ось, по которой рука ушла дальше
 * (в разах, а не в пикселях).
 *
 * Прямой aspect-lock в boundsFromResizeHandle сжимает объект, если тянуть не
 * строго по диагонали — отсюда старая жалоба «увеличить можно только по
 * диагонали». Простой max(sx, sy) это чинил, но ломал уменьшение: потянув угол
 * вбок внутрь, объект не уменьшался вовсе. Доминирующая ось работает в обе
 * стороны одинаково.
 */
export function cornerScaleFromFreeBounds(start: Bounds, free: Bounds): number {
  const sx = start.w > 0 ? free.w / start.w : 1;
  const sy = start.h > 0 ? free.h / start.h : 1;
  const safeX = Math.max(0.05, sx);
  const safeY = Math.max(0.05, sy);
  const dominant = Math.abs(Math.log(safeX)) >= Math.abs(Math.log(safeY)) ? safeX : safeY;
  return Math.max(0.05, dominant);
}

/** Пропорциональные bounds от якоря ручки (как scaleTextPatchUniform для группы). */
export function boundsScaledFromHandle(
  start: Bounds,
  handle: ResizeHandleId,
  scale: number,
  altKey = false
): Bounds {
  // Минимум ограничиваем масштабом, а не каждой стороной отдельно: иначе у
  // вытянутого объекта короткая сторона упирается в 16 раньше длинной,
  // и пропорции на маленьких размерах всё-таки перекашиваются.
  const minScale = Math.max(
    MIN_RESIZE_SIZE / Math.max(1, start.w),
    MIN_RESIZE_SIZE / Math.max(1, start.h)
  );
  const safeScale = Math.max(scale, minScale);
  const w = start.w * safeScale;
  const h = start.h * safeScale;
  const startRight = start.x + start.w;
  const startBottom = start.y + start.h;
  const startCx = start.x + start.w / 2;
  const startCy = start.y + start.h / 2;
  let x: number;
  let y: number;
  if (altKey) {
    x = startCx - w / 2;
    y = startCy - h / 2;
  } else {
    x = handle.includes("w") ? startRight - w : start.x;
    y = handle.includes("n") ? startBottom - h : start.y;
  }
  return clampBounds({ x, y, w, h });
}

export function scaledFontSize(startFontSize: number, scale: number): number {
  const next = startFontSize * scale;
  return Math.round(Math.min(TEXT_FONT_MAX, Math.max(TEXT_FONT_MIN, next)) * 10) / 10;
}

function readTextLikeFontSize(obj: SceneObject): number | undefined {
  if (obj.type !== "text" && obj.type !== "sticky") return undefined;
  const style = obj.style as { fontSize?: unknown } | undefined;
  if (typeof style?.fontSize === "number" && Number.isFinite(style.fontSize) && style.fontSize > 0) {
    return style.fontSize;
  }
  return obj.type === "sticky" ? 15 : 18;
}

export function captureObjectSnapshot(obj: SceneObject): ObjectTransformSnapshot {
  const snap: ObjectTransformSnapshot = {
    x: obj.x,
    y: obj.y,
    w: typeof obj.w === "number" ? obj.w : 0,
    h: typeof obj.h === "number" ? obj.h : 0,
  };
  if (typeof obj.rotation === "number" && Number.isFinite(obj.rotation)) {
    snap.rotation = obj.rotation;
  } else if (usesRotationField(obj)) {
    // Объект поворачивать можно, но его ещё ни разу не поворачивали. Без этого
    // нуля в обратной правке не было бы поля rotation — и Ctrl+Z не возвращал
    // объект в исходное положение, он так и оставался повёрнутым.
    snap.rotation = 0;
  }
  if (Array.isArray(obj.points)) {
    snap.points = (obj.points as [number, number][]).map(([px, py]) => [px, py]);
  }
  const fontSize = readTextLikeFontSize(obj);
  if (typeof fontSize === "number") {
    snap.fontSize = fontSize;
  }
  if (obj.type === "text" && obj.meta && typeof obj.meta === "object") {
    snap.meta = { ...(obj.meta as Record<string, unknown>) };
  }
  if (obj.type === "text") {
    const runs = objectRuns(obj);
    // Храним, только если у кусков есть свой размер: иначе масштабировать нечего.
    if (runs && runs.some((r) => r.fontSize)) snap.runs = runs.map((r) => ({ ...r }));
  }
  return snap;
}

export function snapshotToPatch(snap: ObjectTransformSnapshot): Record<string, unknown> {
  const patch: Record<string, unknown> = {
    x: snap.x,
    y: snap.y,
    w: snap.w,
    h: snap.h,
  };
  if (typeof snap.rotation === "number") {
    patch.rotation = snap.rotation;
  }
  if (snap.points) {
    patch.points = snap.points.map(([px, py]) => [px, py]);
  }
  if (typeof snap.fontSize === "number" && Number.isFinite(snap.fontSize)) {
    patch.style = {
      fontSize: Math.min(TEXT_FONT_MAX, Math.max(TEXT_FONT_MIN, snap.fontSize)),
    };
  }
  if (snap.meta) {
    patch.meta = { ...snap.meta };
  }
  if (snap.runs) {
    patch.runs = snap.runs.map((r) => ({ ...r }));
  }
  return patch;
}

export function patchFromObject(obj: SceneObject): Record<string, unknown> {
  const patch = snapshotToPatch(captureObjectSnapshot(obj));
  if (obj.type === "text" && obj.meta && typeof obj.meta === "object") {
    patch.meta = { ...(obj.meta as Record<string, unknown>) };
  }
  return patch;
}
